import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";
import {PlayerAPI} from "@/api/player";
import "./index.scss";

const number = (value) =>
	value == null
		? "—"
		: Number(value).toLocaleString("it-IT", {maximumFractionDigits: 1});
export default function PlayerStatsContainer({
	type,
	player,
	includePreparation = false,
}) {
	const seasonId = useSelector((state) => state?.season?.id);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	useEffect(() => {
		let active = true;
		async function load() {
			setLoading(true);
			setError(false);
			setRows([]);
			try {
				const fromDate = moment().startOf("isoWeek").format("YYYY-MM-DD");
				const toDate = moment().format("YYYY-MM-DD");
				const response =
					type === "rpe"
						? await PlayerAPI.listRPE({
								playerId: player.id,
								seasonId,
								fromDate,
								toDate,
								includePreparation,
							})
						: await PlayerAPI.playerListTQR(player.id, {fromDate, toDate});
				if (active)
					setRows(
						(response || []).filter((r) =>
							moment(r.rpeDate || r.tqrDate).isSameOrBefore(moment()),
						),
					);
			} catch {
				if (active) setError(true);
			} finally {
				if (active) setLoading(false);
			}
		}
		if (player?.id) load();
		return () => {
			active = false;
		};
	}, [player?.id, seasonId, type, includePreparation]);
	const known = rows.filter(
		(r) =>
			Number.isFinite(Number(type === "rpe" ? r.rpe : r.tqrValue)) &&
			Number(type === "rpe" ? r.rpe : r.tqrValue) >= (type === "rpe" ? 1 : 6) &&
			Number(type === "rpe" ? r.rpe : r.tqrValue) <= (type === "rpe" ? 10 : 20),
	);
	const average = known.length
		? known.reduce(
				(sum, r) => sum + Number(type === "rpe" ? r.rpe : r.tqrValue),
				0,
			) / known.length
		: null;
	return (
		<section className="player-current-measures">
			<header>
				<strong>
					{type === "rpe" ? "Carico attuale" : "Recupero attuale"}
				</strong>
				<span>
					Questa settimana · {moment().startOf("isoWeek").format("DD/MM")}–
					{moment().format("DD/MM")}
				</span>
			</header>
			{loading ? (
				<p role="status">Caricamento…</p>
			) : error ? (
				<p role="alert">Impossibile caricare le misure.</p>
			) : !rows.length ? (
				<p>Nessuna misura registrata nella settimana.</p>
			) : (
				<>
					<div className="player-current-measures__summary">
						{type === "rpe" ? (
							<>
								<div>
									<strong>
										{number(
											known.some((r) => r.trainingLoad != null)
												? known.reduce(
														(sum, r) => sum + (r.trainingLoad ?? 0),
														0,
													)
												: null,
										)}
									</strong>
									<span>Carico totale</span>
								</div>
								<div>
									<strong>
										{number(average)}
										<small>/10</small>
									</strong>
									<span>RPE medio</span>
								</div>
							</>
						) : (
							<>
								<div>
									<strong>
										{number(average)}
										<small>/20</small>
									</strong>
									<span>TQR medio</span>
								</div>
								<span
									className={
										"player-current-measures__status " +
										(average == null
											? ""
											: average < 13
												? "low"
												: average >= 15
													? "good"
													: "fair")
									}
								>
									{average == null
										? "Dato non disponibile"
										: average < 13
											? "Recupero basso"
											: average >= 15
												? "Buon recupero"
												: "Recupero discreto"}
								</span>
							</>
						)}
					</div>
					<details className="player-current-measures__details">
						<summary>
							Dettaglio rilevazioni <span>{rows.length}</span>
						</summary>
						<div className="player-current-measures__table">
							<table>
								<thead>
									<tr>
										<th>Data</th>
										{type === "rpe" ? (
											<>
												<th>Attività</th>
												<th>Minuti</th>
												<th>RPE</th>
												<th>Carico</th>
											</>
										) : (
											<>
												<th>TQR</th>
												<th>Recupero percepito</th>
											</>
										)}
									</tr>
								</thead>
								<tbody>
									{rows.map((r) => {
										const tqr = Number(r.tqrValue);
										const valid = known.includes(r);
										return (
											<tr key={r.id}>
												<td>
													{moment(r.rpeDate || r.tqrDate).format("DD/MM")}
												</td>
												{type === "rpe" ? (
													<>
														<td>{r.trainingId ? "Allenamento" : "Partita"}</td>
														<td>{number(r.eventDuration)}</td>
														<td>{valid ? number(r.rpe) : "—"}</td>
														<td>{valid ? number(r.trainingLoad) : "—"}</td>
													</>
												) : (
													<>
														<td>{valid ? number(tqr) : "—"}</td>
														<td>
															<span
																className={
																	"player-current-measures__status " +
																	(!valid
																		? ""
																		: tqr < 13
																			? "low"
																			: tqr >= 15
																				? "good"
																				: "fair")
																}
															>
																{!valid
																	? "Dato non valido"
																	: tqr < 13
																		? "Basso"
																		: tqr >= 15
																			? "Buono"
																			: "Discreto"}
															</span>
														</td>
													</>
												)}
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</details>
				</>
			)}
		</section>
	);
}
